import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LifeBuoy, Loader2, Inbox, RefreshCw, Mail, Phone, Building2, Search, Trash2 } from "lucide-react";
import {
  adminListTickets, adminGetTicket, adminListMessages,
  adminPostMessage, adminSetStatus, adminUnreadCount,
  adminDeleteMessage, adminDeleteTicket,
} from "@/api/support";
import SupportChat from "@/components/assistant/SupportChat";
import { cn } from "@/lib/cn";
import { toastError, toastSuccess } from "@/lib/toast";
import LoadError from "@/components/dashboard/LoadError";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import ExpandableText from "@/components/ui/ExpandableText";

const STATUS_BADGE = {
  open: "bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300",
  in_progress: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300",
  resolved: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
  closed: "bg-slate-200 text-slate-500 dark:bg-white/10 dark:text-slate-400",
};
const STATUS_LABEL = { open: "Open", in_progress: "In progress", resolved: "Resolved", closed: "Closed" };
const FILTERS = [
  { key: "", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];
const NEXT_STATUS = ["open", "in_progress", "resolved", "closed"];
const PAGE_SIZE = 25;

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [detailNonce, setDetailNonce] = useState(0);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState("updated_at");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  // The ticket awaiting a delete confirmation, and the in-flight flag that
  // keeps the dialog's button from firing a second DELETE.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingTicket, setDeletingTicket] = useState(false);

  // Only the newest request may write. The 20s poll and a filter/search change
  // race constantly, and a slow earlier response landing last would repaint the
  // list under whichever chip is now highlighted.
  const reqRef = useRef(0);

  const refreshList = useCallback(async () => {
    const seq = ++reqRef.current;
    try {
      const data = await adminListTickets({
        status: filter || undefined,
        search: searchTerm || undefined,
        sort,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      if (seq !== reqRef.current) return;
      // Tolerates the older bare-array shape as well as the paged envelope.
      const rows = Array.isArray(data) ? data : (data?.tickets || []);
      setTickets(rows);
      setTotal(Number.isFinite(data?.total) ? data.total : rows.length);
      setError(null);
    } catch (e) {
      if (seq !== reqRef.current) return;
      // This is the ONLY loader for the tab and it used to swallow everything,
      // so a broken support inbox rendered as "no tickets" — indistinguishable
      // from a quiet day. Keep whatever rows we already have and say so.
      setError((e && e.message) || "Tickets could not be refreshed.");
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [filter, searchTerm, sort, page]);

  // The unread badge has to come from the SERVER once the list is paged — a
  // total summed from the rows on screen counts one page, not the inbox. The
  // endpoint already existed and nothing called it.
  const refreshUnread = useCallback(async () => {
    try {
      const res = await adminUnreadCount();
      setUnreadTotal(Number(res?.count ?? res?.unread ?? 0) || 0);
    } catch (e) { /* the badge is an adornment; the list carries the errors */ }
  }, []);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Any change to the criteria means page 1 — staying on page 4 of a narrower
  // result set shows an empty inbox and reads as "no tickets".
  useEffect(() => { setPage(1); }, [filter, searchTerm, sort]);

  useEffect(() => {
    setLoading(true);
    refreshList();
    refreshUnread();
    const id = setInterval(() => { refreshList(); refreshUnread(); }, 20000);
    return () => clearInterval(id);
  }, [refreshList, refreshUnread]);

  useEffect(() => {
    if (!selectedId) { setSelected(null); setDetailError(null); return undefined; }
    let cancelled = false;
    setDetailError(null);
    adminGetTicket(selectedId)
      .then((t) => { if (!cancelled) setSelected(t); })
      .catch((e) => {
        if (cancelled) return;
        // Clearing `selected` alone fell through to the "Select a ticket"
        // placeholder, so a 404/403/500 on the detail read was indistinguishable
        // from a click that never registered.
        setSelected(null);
        setDetailError((e && e.message) || "Couldn't open this ticket.");
      });
    return () => { cancelled = true; };
  }, [selectedId, detailNonce]);

  const adminApi = useMemo(() => ({
    listMessages: (id) => adminListMessages(id),
    postMessage: async (id, body) => {
      const r = await adminPostMessage(id, body);
      refreshList();
      adminGetTicket(id).then(setSelected).catch(() => {});
      return r;
    },
    // Handed to SupportChat so the panel stays generic — it knows there IS a
    // delete, not who is allowed to perform one. The backend decides that.
    deleteMessage: async (id, messageId) => {
      const r = await adminDeleteMessage(id, messageId);
      refreshList();
      return r;
    },
  }), [refreshList]);

  const changeStatus = async (status) => {
    if (!selected) return;
    try {
      const updated = await adminSetStatus(selected.id, status);
      // Both the detail pane and the list row are repainted from the server's
      // answer, so the status on screen is the stored one — and the toast fires
      // only once that answer has arrived.
      setSelected(updated);
      refreshList();
      toastSuccess(`Ticket status updated to "${STATUS_LABEL[status] || status}".`);
    } catch (e) {
      // Swallowing this left the old status on screen, so a failed change was
      // indistinguishable from a successful one.
      toastError((e && e.message) || "Couldn't change the ticket status.");
    }
  };

  // Deleting a ticket takes the whole conversation with it, so it is confirmed
  // first and the dialog says exactly how much goes.
  const deleteTicket = async () => {
    if (!pendingDelete || deletingTicket) return;
    setDeletingTicket(true);
    try {
      await adminDeleteTicket(pendingDelete.id);
      setPendingDelete(null);
      // Clear the detail pane only after the server confirmed — a ticket that
      // failed to delete must stay open in front of the operator.
      if (selectedId === pendingDelete.id) { setSelectedId(null); setSelected(null); }
      toastSuccess("Ticket deleted.");
      refreshList();
      refreshUnread();
    } catch (e) {
      toastError((e && e.message) || "Couldn't delete this ticket.");
    } finally {
      setDeletingTicket(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-800 dark:text-white">
            <LifeBuoy className="text-brand-500" size={24} /> Support Inbox
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Live tickets from testers and admins.{unreadTotal > 0 && <span className="ml-1 font-semibold text-rose-500">{unreadTotal} unread</span>}
          </p>
        </div>
        <button type="button" onClick={refreshList}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:border-brand-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <LoadError message={error} onRetry={refreshList} />

      {/* Filters — status chips, plus the search and ordering the inbox never
          had. Status alone is not a way to find a ticket. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)}
              className={cn("rounded-full px-3.5 py-1.5 text-xs font-semibold transition",
                filter === f.key
                  ? "bg-gradient-to-r from-brand-600 to-violet-600 text-white shadow-soft"
                  : "border border-slate-200 bg-white text-slate-600 hover:border-brand-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-300")}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-64">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email or message…"
              aria-label="Search tickets"
              maxLength={100}
              className="h-9 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort tickets"
            className="h-9 rounded-xl border border-slate-200 bg-white px-2 text-sm text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <option value="updated_at">Recently updated</option>
            <option value="created_at">Newest first</option>
          </select>
        </div>
      </div>

      {/* THE layout fix. This was `lg:grid-cols-[minmax(0,360px)_1fr]`, and
          `1fr` is shorthand for `minmax(auto, 1fr)` — an AUTO minimum, which
          means the detail column could never be narrower than its own
          min-content. One long unbroken token in a reply or in the original
          request therefore set a floor wider than the viewport: the grid grew,
          the 360px list column was squeezed against it, and the ticket list
          became unusable. `minmax(0, 1fr)` gives the column a real zero
          minimum, so it takes the space that is left and its contents wrap
          inside it.

          items-start, so the two columns size independently: without it the
          shorter card stretched to the taller one and the list's own scroll
          area was measured against the conversation's height. */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
        {/* List. min-w-0 for the same reason as the column itself — a grid item
            also defaults to an auto minimum. */}
        <div data-probe="list" className="card max-h-[70vh] min-w-0 overflow-y-auto p-2">
          {loading ? (
            <div className="grid place-items-center py-16 text-slate-400"><Loader2 className="animate-spin" size={20} /></div>
          ) : error && tickets.length === 0 ? null : tickets.length === 0 ? (
            <div className="grid place-items-center gap-2 py-16 text-center text-sm text-slate-400">
              <Inbox size={22} /> No tickets here.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {tickets.map((t) => (
                <li key={t.id}>
                  <button type="button" onClick={() => setSelectedId(t.id)}
                    className={cn("w-full rounded-xl border p-3 text-left transition",
                      selectedId === t.id
                        ? "border-brand-400 bg-brand-50/60 dark:border-brand-500/40 dark:bg-brand-500/10"
                        : "border-slate-200 bg-white hover:border-brand-300 dark:border-white/10 dark:bg-white/5")}>
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-slate-800 dark:text-white">{t.name}</span>
                      <span className={cn("shrink-0 rounded-md px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase", STATUS_BADGE[t.status])}>{STATUS_LABEL[t.status]}</span>
                    </div>
                    {/* The preview is clamped to two lines to keep rows an even
                        height; overflow-wrap:anywhere stops a pasted URL in the
                        ticket body setting this row's min-content width. */}
                    <p className="mt-1 line-clamp-2 min-w-0 break-words [overflow-wrap:anywhere] text-xs text-slate-500 dark:text-slate-400">{t.message}</p>
                    <div className="mt-1.5 flex items-center gap-2 text-[0.625rem] text-slate-400">
                      <span>#{t.id.slice(0, 8)}</span>
                      <span>· {t.messageCount} msg</span>
                      {t.unreadCount > 0 && <span className="rounded-full bg-rose-500 px-1.5 py-0.5 font-bold text-white">{t.unreadCount} new</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Pager. Anything past the first page was previously unreachable — the
            endpoint capped at 200 with no offset. */}
        {pages > 1 && (
          <div className="-mt-3 flex items-center justify-between gap-3 px-1 text-xs text-slate-400 lg:col-start-1">
            <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
                className="rounded-lg px-2.5 py-1 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/5">‹</button>
              <span className="text-sm text-slate-500">{page} / {pages}</span>
              <button type="button" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}
                className="rounded-lg px-2.5 py-1 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/5">›</button>
            </div>
          </div>
        )}

        {/* Detail */}
        <div data-probe="detail" className="card min-w-0 max-w-full p-4">
          {detailError ? (
            <div className="grid h-full min-h-[300px] place-items-center">
              <LoadError message={detailError} onRetry={() => setDetailNonce((n) => n + 1)} className="mb-0" />
            </div>
          ) : !selected ? (
            <div className="grid h-full min-h-[300px] place-items-center gap-2 text-center text-sm text-slate-400">
              <LifeBuoy size={26} /> Select a ticket to view the conversation.
            </div>
          ) : (
            <motion.div key={selected.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="min-w-0 space-y-4">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  {/* The reporter's name and email are user-supplied and can be
                      long enough to have no break opportunity at all. */}
                  <h2 className="min-w-0 break-words [overflow-wrap:anywhere] text-lg font-bold text-slate-800 dark:text-white">{selected.name}</h2>
                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                    {selected.email && <span className="inline-flex min-w-0 items-center gap-1 break-all"><Mail size={12} className="shrink-0" /> {selected.email}</span>}
                    {selected.phone && <span className="inline-flex items-center gap-1"><Phone size={12} className="shrink-0" /> {selected.phone}</span>}
                    <span className="inline-flex items-center gap-1"><Building2 size={12} className="shrink-0" /> via {selected.source === "landing_page" ? "website" : "in-app"}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {NEXT_STATUS.map((s) => (
                    <button key={s} type="button" onClick={() => changeStatus(s)}
                      className={cn("rounded-lg px-2.5 py-1 text-[0.6875rem] font-semibold transition",
                        selected.status === s ? STATUS_BADGE[s] : "border border-slate-200 text-slate-500 hover:border-brand-300 dark:border-white/10 dark:text-slate-400")}>
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                  {/* Separated from the status row by a divider: it is not one
                      more state to move the ticket into, it removes it. */}
                  <span className="mx-0.5 h-4 w-px bg-slate-200 dark:bg-white/10" aria-hidden="true" />
                  <button type="button" onClick={() => setPendingDelete(selected)}
                    title="Delete this ticket"
                    aria-label={`Delete ticket from ${selected.name}`}
                    className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 transition hover:bg-rose-50 hover:text-rose-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/50 dark:hover:bg-rose-500/10">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* The original request is whatever the reporter typed — it used
                  to be dropped straight into this div with no wrapping rules
                  and no height bound, so a long or newline-heavy request both
                  lost its paragraphs AND stretched the card. ExpandableText is
                  the same renderer the changelog uses: it preserves the line
                  breaks, wraps anywhere it must, shows six lines and offers the
                  rest behind "View full request". Nothing is truncated. */}
              <div data-probe="request" className="min-w-0 rounded-xl bg-slate-100 px-3.5 py-3 text-sm text-slate-600 dark:bg-white/5 dark:text-slate-300">
                <p className="mb-1 text-[0.625rem] font-bold uppercase tracking-wide text-slate-400">Original request</p>
                <ExpandableText
                  text={selected.message}
                  expandable
                  moreLabel="View full request"
                  lessLabel="Show less"
                  className="text-sm"
                />
              </div>

              <SupportChat
                ticket={selected}
                role="admin"
                api={adminApi}
                locked={selected.status === "closed"}
                heightClass="h-[clamp(280px,46vh,440px)]"
              />
            </motion.div>
          )}
        </div>
      </div>

      {/* Deleting a ticket removes the conversation with it, and there is no
          soft-delete anywhere in this product to undo it from — so the dialog
          says exactly what goes, and nothing happens if the operator cancels. */}
      <ConfirmDialog
        open={pendingDelete}
        title={pendingDelete ? `Delete the ticket from ${pendingDelete.name}?` : ""}
        body={pendingDelete
          ? `This permanently removes the ticket and all ${pendingDelete.messageCount ?? "its"} message${pendingDelete.messageCount === 1 ? "" : "s"} in its conversation. This cannot be undone.`
          : ""}
        confirmLabel="Delete ticket"
        busy={deletingTicket}
        onConfirm={deleteTicket}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
